import { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { fetchSuppliersWithSection, searchProductsBySection } from '@/lib/api';
import type { PackageSection } from '@/lib/api';
import type { GiftProduct, GiftSupplier } from '@/lib/types';

type Tab = 'alles' | PackageSection;

const TABS: { id: Tab; label: string }[] = [
  { id: 'alles', label: 'Alle' },
  { id: 'verpakking', label: 'Verpakking' },
  { id: 'gadget', label: 'Gadget' },
  { id: 'drank', label: 'Drank' },
  { id: 'food', label: 'Food' },
];

interface Props {
  onAdd: (product: GiftProduct) => void | Promise<void>;
  disabled?: boolean;
  initialSection?: Tab;
  /** Bij true: tabs verbergen omdat sectie van buiten af vastgezet wordt. */
  lockSection?: boolean;
}

const fieldCls =
  'w-full rounded-md border border-bmade-200 bg-white text-sm text-bmade-950 outline-none transition-colors focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white dark:focus:border-accent-400';

export function CatalogSearch({ onAdd, disabled, initialSection, lockSection }: Props) {
  const [suppliers, setSuppliers] = useState<GiftSupplier[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<Tab>(initialSection ?? 'alles');
  const [products, setProducts] = useState<GiftProduct[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSuppliersWithSection(tab)
      .then((list) => {
        setSuppliers(list);
        if (supplierId && !list.some((s) => s.id === supplierId)) {
          setSupplierId('');
        }
      })
      .catch(() => setSuppliers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (initialSection) setTab(initialSection);
  }, [initialSection]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      searchProductsBySection(tab, query, supplierId || undefined)
        .then(setProducts)
        .catch(() => setProducts([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, supplierId, tab]);

  return (
    <div className="space-y-3">
      {!lockSection && (
        <div className="flex flex-wrap gap-1 rounded-md border border-bmade-200 bg-bmade-50/60 p-0.5 text-xs dark:border-bmade-800 dark:bg-bmade-900/40">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`flex-1 rounded px-2 py-1 font-medium transition-colors ${
                tab === id
                  ? 'bg-white text-bmade-950 shadow-sm dark:bg-bmade-950 dark:text-white'
                  : 'text-bmade-600 hover:text-bmade-900 dark:text-bmade-400 dark:hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <select
        value={supplierId}
        onChange={(e) => setSupplierId(e.target.value)}
        className={`${fieldCls} px-2 py-1.5`}
      >
        <option value="">Alle leveranciers</option>
        {suppliers.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-bmade-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek artikel of artikelnr..."
          className={`${fieldCls} py-1.5 pl-7 pr-2`}
        />
      </div>

      <div className="space-y-1">
        {loading && <p className="text-xs text-bmade-500">Zoeken...</p>}
        {!loading && products.length === 0 && (
          <p className="text-xs text-bmade-500">
            {tab === 'verpakking' && supplierId === ''
              ? 'Nog geen verpakkings-producten in catalogus. DS Smith en Baka moeten geïmporteerd worden.'
              : 'Geen resultaten.'}
          </p>
        )}
        {products.map((p) => (
          <div
            key={p.id}
            className="flex items-start justify-between gap-2 rounded-md border border-bmade-200 bg-white p-2 text-xs transition-colors hover:border-bmade-300 dark:border-bmade-800 dark:bg-bmade-950/40 dark:hover:border-bmade-700"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-bmade-950 dark:text-white">{p.name}</div>
              <div className="text-bmade-600 dark:text-bmade-400">
                {p.supplier?.name ?? '—'}
                {p.brand && ` · ${p.brand}`}
              </div>
              <div className="mt-0.5 text-bmade-500 dark:text-bmade-500">
                EUR {p.unit_purchase_price.toFixed(2)} · colli {p.vpe} ·{' '}
                {p.btw_category === 'low' ? '9%' : '21%'}
              </div>
              {p.article_number && (
                <div className="text-bmade-400 dark:text-bmade-600">Art.nr {p.article_number}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => onAdd(p)}
              disabled={disabled}
              className="shrink-0 rounded-md border border-bmade-200 p-1.5 text-accent-600 transition-colors hover:border-accent-500 hover:bg-accent-50 disabled:opacity-30 dark:border-bmade-700 dark:text-accent-400 dark:hover:border-accent-400 dark:hover:bg-accent-500/10"
              title="Toevoegen aan pakket"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
